import type { ReactNode } from 'react';
import { useThemeMode } from '@/shared/config/theme/theme-mode-context';

/**
 * The template's `useTheme`, answered by the panel's own appearance setting.
 *
 * ⚠️ **The original kept its own state and toggled a `.dark` class on `<html>`.** This panel
 * switches appearance by rewriting every `--color-*` on `:root` (§7), because the palette is a
 * document the administration edits and a class cannot carry it. Two systems both claiming to own
 * "is it dark" is one system too many: the template's copy would have written `theme` to
 * localStorage, added a class nothing reads, and left the actual colours untouched — a toggle that
 * appears to do nothing.
 *
 * So the names stay and the state is ours. `ThemeToggleButton` and `AppHeader` import `useTheme`
 * from here and work unchanged, driving the same setting the header's three-way control drives.
 *
 * ⚠️ Toggling lands on an explicit `light` or `dark`, never back on `system`. A person reaching for
 * a toggle is asking for the other one, not for the machine's opinion; `system` stays reachable from
 * the three-way control where it is a visible choice rather than a hidden third state in a switch.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  // ⚠️ A pass-through. The state lives in `ThemeModeProvider`, mounted once in `app/providers.tsx`;
  // this exists only so the template's `<ThemeProvider>` wrapper keeps compiling where it appears.
  return <>{children}</>;
}

export function useTheme() {
  const { resolved, setMode } = useThemeMode();

  return {
    theme: resolved,
    toggleTheme: () => setMode(resolved === 'dark' ? 'light' : 'dark'),
  };
}
